// ---- Writes --------------------------------------------------------------

import {
  useMutation,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import {
  serviceApi,
  onPath,
  onKeys,
  type InstanceAction,
} from '../_shared';

export function useInstanceAction(
  serviceId: string,
  instanceId: string,
  options?: UseMutationOptions<unknown, unknown, InstanceAction>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (perform: InstanceAction) =>
      (await serviceApi.post(onPath(serviceId, `instances/${instanceId}/perform`), { perform }))
        .data,
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: onKeys.instance(serviceId, instanceId) });
      qc.invalidateQueries({ queryKey: onKeys.instances(serviceId) });
      options?.onSuccess?.(...args);
    },
  });
}

export function useCreateInstance(
  serviceId: string,
  options?: UseMutationOptions<unknown, unknown, Record<string, unknown>>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Record<string, unknown>) =>
      (await serviceApi.post(onPath(serviceId, 'instances'), payload)).data,
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: onKeys.instances(serviceId) });
      options?.onSuccess?.(...args);
    },
  });
}

export function useUpdateInstance(
  serviceId: string,
  instanceId: string,
  options?: UseMutationOptions<unknown, unknown, Record<string, unknown>>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Record<string, unknown>) =>
      (await serviceApi.patch(onPath(serviceId, `instances/${instanceId}`), payload)).data,
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: onKeys.instance(serviceId, instanceId) });
      options?.onSuccess?.(...args);
    },
  });
}
